// =============================================================================
// CERFA MODÈLES (TEF) — Liste lecture seule des modèles officiels ANAH/MPA
// =============================================================================
// Source unique : filesystem backend (backend/assets/documents_sources_tef/),
// exposé par GET /api/metier/tef/cerfa-modeles. Aucune collection Mongo dupliquée
// (contrairement à DocumentsSourcesManager qui gère la collection documents_sources
// pour les tenants CEE/OMV). Téléchargement via /api/metier/tef/cerfa-source/{code}.
// =============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { FileText, Download, RefreshCw, Loader2, Info, File, AlertTriangle, Upload } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CerfaModelesTef({ tenantId = "tef" }) {
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [uploadingCode, setUploadingCode] = useState(null);
  const fileInputs = useRef({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": tenantId || "tef",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/tef/cerfa-modeles`, { headers: getAuthHeaders() });
      if (res.ok) {
        setDocuments(await res.json());
      } else {
        toast.error("Erreur lors du chargement des modèles CERFA");
      }
    } catch (error) {
      console.error("Erreur chargement CERFA:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownload = async (doc) => {
    try {
      const res = await fetch(`${API}/api/metier/tef/cerfa-source/${doc.code}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = doc.fichier_nom || `${doc.code}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Téléchargement en cours");
      } else {
        toast.error("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleReplace = async (doc, file) => {
    if (!file) return;
    if (file.type && file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Le fichier doit être un PDF");
      return;
    }
    setUploadingCode(doc.code);
    try {
      const form = new FormData();
      form.append("fichier", file);
      const res = await fetch(`${API}/api/metier/tef/cerfa-source/${doc.code}`, {
        method: "POST",
        headers: getAuthHeaders(), // pas de Content-Type : le navigateur pose le boundary
        body: form,
      });
      if (res.ok) {
        toast.success(`Modèle « ${doc.nom} » remplacé`);
        loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lors du remplacement");
      }
    } catch (error) {
      console.error("Erreur remplacement:", error);
      toast.error("Erreur lors du remplacement");
    } finally {
      setUploadingCode(null);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return "-";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} Ko`;
    return `${(kb / 1024).toFixed(1)} Mo`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[var(--oh-text-muted)] animate-spin" />
      </div>
    );
  }

  // Groupement par phase (Avant / Après travaux) pour la lisibilité
  const categories = [...new Set(documents.map((d) => d.categorie))];

  return (
    <div className="space-y-6 p-6" data-testid="cerfa-modeles-tef">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-[var(--oh-text)] flex items-center gap-3">
            <FileText className="w-7 h-7 text-[var(--oh-info-text)]" />
            Document Modèle (CERFA officiels ANAH / MaPrimeAdapt')
          </h1>
          <p className="text-[var(--oh-text-muted)] mt-1">
            Modèles officiels utilisés par la génération et la signature — déposez le
            PDF propre, il est aussitôt celui que le système utilise.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} className="border-[var(--oh-border)]">
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-[var(--oh-accent-bg)] border-[var(--oh-accent)]">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-[var(--oh-accent-text)] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="text-[var(--oh-accent-text)] font-medium">Source unique de la génération</p>
              <p className="text-[var(--oh-accent-text)] mt-1">
                Ces PDF sont la source unique lue par « Préparer &amp; Signer », le
                remplissage automatique (mandat ANAH, demande d'avance) et le plan de
                financement. « Remplacer » écrase le modèle utilisé par le système (une
                sauvegarde horodatée est conservée côté serveur). ⚠️ Le Cerfa 13460 (plan
                de financement) est calibré pour le pré-remplissage : le remplacer par une
                mise en page différente peut décaler les montants — prévenir un dev.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardHeader>
          <CardTitle className="text-[var(--oh-text)] flex items-center gap-2">
            <FileText className="w-5 h-5 text-[var(--oh-accent-text)]" />
            Modèles disponibles
          </CardTitle>
          <CardDescription className="text-[var(--oh-text-muted)]">
            {documents.length} modèle(s) officiel(s)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-[var(--oh-text-secondary)] mx-auto mb-4" />
              <p className="text-[var(--oh-text-muted)] text-lg">Aucun modèle CERFA</p>
            </div>
          ) : (
            categories.map((cat) => (
              <div key={cat} className="space-y-3">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--oh-text-muted)]">
                  {cat}
                </p>
                {documents
                  .filter((d) => d.categorie === cat)
                  .map((doc) => (
                    <div
                      key={doc.code}
                      className="p-4 bg-[var(--oh-bg-surface)] border border-[var(--oh-border)] rounded-lg"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-[var(--oh-info-bg)] rounded-lg">
                            <File className="w-6 h-6 text-[var(--oh-info-text)]" />
                          </div>
                          <div>
                            <h3 className="text-[var(--oh-text)] font-medium">{doc.nom}</h3>
                            <p className="text-[var(--oh-text-secondary)] text-sm">{doc.fichier_nom}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-[var(--oh-text-muted)]">
                              <span>{formatBytes(doc.fichier_taille)}</span>
                              {!doc.exists && (
                                <Badge className="bg-[var(--oh-error-bg)] text-[var(--oh-error-text)] text-xs flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Fichier absent
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(doc)}
                            disabled={!doc.exists}
                            title="Télécharger"
                            aria-label={`Télécharger ${doc.nom}`}
                          >
                            <Download className="w-4 h-4 text-[var(--oh-text-muted)]" />
                          </Button>
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            ref={(el) => (fileInputs.current[doc.code] = el)}
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = ""; // permet de re-sélectionner le même fichier
                              handleReplace(doc, f);
                            }}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputs.current[doc.code]?.click()}
                            disabled={uploadingCode === doc.code}
                            className="border-[var(--oh-accent)] text-[var(--oh-accent-text)]"
                            title="Remplacer le modèle"
                            aria-label={`Remplacer ${doc.nom}`}
                          >
                            {uploadingCode === doc.code ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4 mr-1" />
                            )}
                            Remplacer
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
